import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { apiProcess } from 'app/config/axios-interceptor';
import { createEntitySlice, isRejectedAction, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState: any = {
  loading: false,
  errorMessage: null,
  entities: [],
  entityEvaluates: [],
  preApprovals: [],
  resultEvaluations: [],
  entity: {},
  otherComment: [],
  selfConfirmComplete: [],
  viewing: false,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/evaluates';

// Actions
type IRequestAction = {
  ownerId: string;
  accountTableId: string;
  planPeriodId: number;
};

export const getEntities = createAsyncThunk(
  'category_evaluate/fetch_entity_list',
  async ({ userId, searchText, planPeriodId }: any) => {
    return apiProcess.get<any[]>(`${apiUrl}/evaluate-categories`, {
      params: {
        userId,
        searchText,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getCurrentStatusProcess = createAsyncThunk(
  'category_evaluate/fetch_current_process',
  async ({ userId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any[]>(`${apiUrl}/current-level-function`, {
      params: {
        userId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const fetchCurrentStatusProcess = createAsyncThunk(
  'category_evaluate/fetch_current_status_process',
  async ({ ownerId, year, planPeriodId }: any) => {
    const requestUrl = `${apiUrl}/states/detail`;
    return apiProcess.get<any>(requestUrl, {
      params: {
        ownerId,
        year,
        planPeriodId,
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getPreEvaluate = createAsyncThunk(
  'category_evaluate/fetch_pre_entity_list',
  async ({ ownerId, planPeriodId }: any) => {
    return apiProcess.get<any[]>(`${apiUrl}/evaluate-versions`, {
      params: {
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const saveDraft = createAsyncThunk(
  'category_evaluate/save_entity_draft',
  async (payload: any) => {
    return apiProcess.post<any>(`${apiUrl}/draft-save`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesEvaluate = createAsyncThunk(
  'category_evaluate/fetch_entity_list_evaluate',
  async ({ ownerId, accountTableId, planPeriodId }: IRequestAction, { signal }) => {
    const controller = new AbortController();
    signal.addEventListener('abort', () => controller.abort()); // Liên kết AbortController với signal của thunk
    return apiProcess.get<any[]>(`${apiUrl}/evaluate-results`, {
      params: {
        userId: ownerId,
        ownerId: accountTableId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
      signal: controller.signal,
    });
  },
  { serializeError: serializeAxiosError },
);

export const cancelProcess = createAsyncThunk(
  'category_evaluate/cancel_evaluate_process',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/cancel`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const completeProcess = createAsyncThunk(
  'category_evaluate/complete_evaluate_process',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/agree`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const rejectProcess = createAsyncThunk(
  'category_evaluate/complete_evaluate_process',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/refuse`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const submitEvaluate = createAsyncThunk(
  'category_evaluate/evaluate_entity_list',
  async (payload: any) => {
    return apiProcess.post(`${apiUrl}/submit-approval`, payload);
  },
  { serializeError: serializeAxiosError },
);

export const getOtherComment = createAsyncThunk(
  'category_evaluate/other_comment',
  async ({ itemId, ownerId, planPeriodId }: any) => {
    return apiProcess.get<any>(`${apiUrl}/prefeedback-item`, {
      params: {
        itemId,
        ownerId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getLimitedProcessEvaluate = createAsyncThunk(
  'category_evaluate/list_process_evaluate_limited',
  async ({ userId, planPeriodId }: any) => {
    return apiProcess.get(`${apiUrl}/max-order-level`, {
      params: {
        userId,
        planPeriodId,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getResultConfirmComplete = createAsyncThunk(
  'confirm_complete/fetch_result_complete',
  async ({ userId, ownerId, planPeriodId, searchText }: any) => {
    return apiProcess.get<any[]>(`${apiUrl}/evaluate-general`, {
      params: {
        userId,
        ownerId,
        planPeriodId,
        searchText,
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

// slice

export const CategoryEvaluationSlice = createEntitySlice({
  name: 'categoryEvaluation',
  initialState,
  reducers: {
    resetApprovalEntity(state) {
      return { ...state, entityEvaluates: [] };
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getOtherComment), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          otherComment: data,
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(getPreEvaluate), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          preApprovals: data,
        };
      })
      .addMatcher(isFulfilled(fetchCurrentStatusProcess), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entity: data,
        };
      })
      .addMatcher(isFulfilled(getCurrentStatusProcess), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entity: data,
        };
      })
      .addMatcher(isFulfilled(getEntitiesEvaluate), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entityEvaluates: data,
        };
      })
      .addMatcher(isFulfilled(getResultConfirmComplete), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          resultEvaluations: data,
        };
      })
      .addMatcher(isFulfilled(saveDraft), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(completeProcess), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(rejectProcess), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(submitEvaluate), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
      })
      .addMatcher(isPending(saveDraft, submitEvaluate, completeProcess, rejectProcess), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(getCurrentStatusProcess, fetchCurrentStatusProcess), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(getResultConfirmComplete), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(getEntities, getEntitiesEvaluate, getPreEvaluate), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isRejected(saveDraft, getEntities), (state, action) => {
        state.loading = false;
        state.updating = false;
        state.updateSuccess = false;
        state.errorMessage = action.error.message;
      });
  },
});

export const { reset, resetApprovalEntity } = CategoryEvaluationSlice.actions;

// Reducer
export default CategoryEvaluationSlice.reducer;
